import { html, LitElement, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Ref } from 'lit/directives/ref.js';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../_ui-core_/base/package/util/a11y';
import { isDesktopWidth, isMobileWidth, LayoutType, layoutType } from '../../_ui-core_/base/package/util/device';
import { UIProductSwitcher } from '../../_ui-core_/components/ui-product-switcher/ui-product-switcher';
import App from '../../app';
import { A11yElementId } from '../../app/package/base/service/a11y/a11y-service';
import type { Favorites } from '../../app/package/base/service/favorites/favorites-service';
import { type LoginObject, LoginStatus } from '../../app/package/base/service/login/login-domain';
import { ModalType } from '../../app/package/base/service/modals/modal-service';
import {
  TrackableEventAction,
  type TrackingEventSource,
} from '../../app/package/base/service/tracking/tracking-service';
import style from './base-page-header.css?inline';
import '../../_ui-core_/components/ui-game-info/ui-favorite-icon';
import '../../_ui-core_/components/ui-header/ui-header';
import '../../_ui-core_/components/ui-header/ui-header-icon-back';
import '../../_ui-core_/components/ui-header/ui-header-icon-notification';
import '../../_ui-core_/components/ui-header/ui-header-icon-switcher';
import '../../_ui-core_/components/ui-header/ui-header-icon-user';
import '../../_ui-core_/components/ui-icons/ui-more-options-icon';
import '../../_ui-core_/components/ui-spinner/ui-spinner';

const CName = 'base-page-header';

export const BasePageHeaderType = {
  DEFAULT: 'default',
  FULL: 'full',
  HIDDEN: 'hidden',
  WITH_ICONS: 'with-icons',
  WITH_STEPPER: 'with-stepper',
} as const;

export type BasePageHeaderType = (typeof BasePageHeaderType)[keyof typeof BasePageHeaderType];

export type CallbackFunction = () => void;

export type BasePageHeaderOptions = {
  gameId?: string;
  provider?: string;
  headerClass?: string;
  isFavoriteShown?: boolean;
  isGoBackShown?: boolean;
  isH1TitleTag?: boolean;
  isLogoShown?: boolean;
  isUserNavShown?: boolean;
  title: string;
  trackingSource?: TrackingEventSource | string;
  optionsFn?: CallbackFunction;
  onBackAction?: CallbackFunction;
};

export type RefElement = Ref<Element>;

/**
 * @prop {BasePageHeaderType} type - The type of the header
 * @prop {BasePageHeaderOptions} options - The options for the header
 * @prop {boolean} secondaryHeader - Whether the header is a secondary header. No logo and no login button used in this case.
 * @prop {boolean} headerOpacity - The opacity of header-background
 */
@customElement(CName)
export class BasePageHeader extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: String }) type: BasePageHeaderType;
  @property({ type: Object }) options?: BasePageHeaderOptions;
  @property({ type: Boolean }) secondaryHeader = false;
  @property({ type: Object }) slideInRef: RefElement;

  @state() private _loginStatus: LoginStatus;
  @state() private _favorites: Favorites = new Set();
  @state() private _hasNewNotifications = false;

  private readonly _subHelper = new SubHelper();
  private readonly _layoutType = layoutType();

  connectedCallback() {
    super.connectedCallback();
    this._subHelper.addSub(window.$app.loginStore, (_) => this._updateLogin(_), true);
    App.instance.login.getFinalLoginStatus().then((status) => {
      if (App.instance.notifications !== undefined && status === LoginStatus.LOGGED_IN) {
        App.instance.notifications
          .getCurrentNewNotificationsCount()
          .then((c) => {
            this._hasNewNotifications = c > 0;
          })
          .catch((err) => {
            window.$app.logger.warn('Error fetching new notifications count', err);
          });
      }
    });
    this._subHelper.addSub(App.instance.favorites.store, (_) => this._updateFavorites(_), true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  render() {
    return html`
      ${this._renderHeaderTypes()}
    `;
  }

  private _renderHeaderTypes() {
    switch (this.type) {
      case BasePageHeaderType.FULL:
        return this._renderFull();
      case BasePageHeaderType.HIDDEN:
        return nothing;
      case BasePageHeaderType.WITH_ICONS:
        return this._renderWithIcons();
      default:
        return this._renderDefault();
    }
  }

  private _renderDefault() {
    const backBtn = this.options?.isGoBackShown ? this._renderGoBack() : nothing;
    const title = this.options?.title ?? undefined;
    return html`
      <ui-header .scrollableContainerRef=${this.slideInRef}>
        ${backBtn !== nothing || title !== undefined ? this._renderDefaultLeft(backBtn) : nothing}
        ${!this.secondaryHeader && title === undefined ? this._renderDefaultMiddle() : nothing}
        ${!this.secondaryHeader ? this._renderDefaultRight() : nothing}
        ${this._renderSkipToMainNav()}
      </ui-header>
    `;
  }

  private _renderDefaultLeft(backBtn: TemplateResult<1> | typeof nothing): TemplateResult<1> {
    return html`
      <div slot="left" class="navigate-back">
        ${backBtn !== nothing ? backBtn : this._renderTitle()}
      </div>
    `;
  }

  private _renderDefaultMiddle(): TemplateResult<1> {
    return html`
      <div slot="left" @click=${() => App.instance.router.navigateToHome()}>
        <div class="logo"></div>
      </div>
    `;
  }

  private _renderDefaultRight(): TemplateResult<1> {
    return html`
      <div slot="right">
        ${this._loginStatus === LoginStatus.LOGGED_OUT ? this._renderLoginButton() : nothing}
        ${this.options?.optionsFn ? this._renderOptions() : nothing}
      </div>
    `;
  }

  private _renderFull() {
    if (this.secondaryHeader) {
      return nothing;
    }
    return html`
      <ui-header .scrollableContainerRef=${this.slideInRef} .isLoggedIn=${this._loginStatus === LoginStatus.LOGGED_IN}>
        <button
          slot="left"
          class=${`logo-with-dropdown ${isMobileWidth() ? 'logo-with-dropdown--mobile' : ''}`}
          @click=${this._showProductSwitcher}
          aria-label="Tipico Games Logo"
          aria-haspopup="true"
        >
          <div class="logo"></div>
          <ui-header-icon-switcher></ui-header-icon-switcher>
        </button>
        <div slot="right" class="right-icons">
          ${
            this._loginStatus === LoginStatus.UNDEFINED || this._loginStatus === LoginStatus.FETCHING
              ? this._renderSpinner()
              : nothing
          }
          ${this._loginStatus === LoginStatus.LOGGED_OUT ? this._renderLoginButton() : nothing}
          ${
            this._loginStatus === LoginStatus.LOGGED_IN
              ? html`
                ${App.instance.featureFlag.showNotifications() ? this._renderNotificationsButton() : nothing}
                ${this._renderAccountButton()}
              `
              : nothing
          }
        </div>
        ${this._renderSkipToMainNav()}
      </ui-header>
    `;
  }

  private _renderSkipToMainNav() {
    if (isDesktopWidth()) {
      return nothing;
    }
    return html`
      <a
        @click=${this._skipToMainNav}
        class="a11y-skip-link"
        href="#a11y-main-navigation"
        slot="right"
      >${window.$app.$t.get(I18n.a11y.nav.skipToMain)}</a>
    `;
  }

  private _renderSpinner() {
    return html`<ui-spinner class="spinner s"></ui-spinner>`;
  }

  private _renderAccountButton() {
    return html`
      <ui-header-icon-user
        @click=${() => this._goToAccount()}
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._goToAccount)}
        aria-label=${window.$app.$t.get(I18n.base.account)}
        class="rounded-icon"
        role="button"
        tabindex="0"
      ></ui-header-icon-user>
    `;
  }

  private _renderNotificationsButton() {
    return html`
      <ui-header-icon-notification
        .showDot=${this._hasNewNotifications}
        @click=${this._goToNotifications}
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._goToNotifications)}
        aria-label=${window.$app.$t.get(I18n.base.notifications)}
        class="rounded-icon"
        role="button"
        tabindex="0"
      ></ui-header-icon-notification>
    `;
  }

  private _renderTitle() {
    if (!this.options?.title) {
      return nothing;
    }
    const title = this.options.title;
    if (this.options.isH1TitleTag === false) {
      return html`<span class="title">${title}</span>`;
    }
    return html`<h1 class="title">${title}</h1>`;
  }

  private _renderWithIcons() {
    const classes = [this.options?.headerClass ?? ''];
    return html`
      <ui-header .scrollableContainerRef=${this.slideInRef} class=${classes.join(' ')}>
        <div slot="left" class="navigate-back">
          ${this.options?.isGoBackShown ? this._renderGoBack() : this._renderTitle()}
        </div>
        <div slot="right">
          ${this._loginStatus === LoginStatus.LOGGED_OUT ? this._renderLoginButton() : nothing}
          ${this._loginStatus === LoginStatus.LOGGED_IN ? this._renderFavoriteIcon() : nothing}
        </div>
        ${this._renderSkipToMainNav()}
      </ui-header>
    `;
  }

  private _renderLoginButton() {
    if (this._layoutType === LayoutType.DESKTOP) {
      return nothing;
    }
    return html`
      <ui-btn
        variant="secondary-outlined"
        size="custom"
        class="login-button"
        data-testid="login"
        @click=${this._handleLoginClick}
      >
        ${window.$app.$t.get(I18n.base.login)}
      </ui-btn>
    `;
  }

  private _renderOptions() {
    return html`
      <ui-more-options-icon
        class="more-options-icon"
        @click=${() => this.options?.optionsFn?.()}
      ></ui-more-options-icon>
    `;
  }

  private _renderGoBack() {
    const onClick = () => {
      if (typeof this.options?.onBackAction !== 'function') {
        App.instance.router.navigateBack();
      } else {
        this.options?.onBackAction();
        this.options.onBackAction = undefined;
      }
    };
    return html`
      <ui-btn
        variant="text"
        size="custom"
        class="header__button"
        @click=${onClick}
        aria-label=${window.$app.$t.get(I18n.base.back)}
      >
        <ui-header-icon-back></ui-header-icon-back>
        ${this._renderTitle()}
      </ui-btn>
    `;
  }

  private _renderFavoriteIcon() {
    if (!this.options?.isFavoriteShown || !this.options?.gameId) {
      return nothing;
    }

    return html`
      <ui-favorite-icon
        tabindex="0"
        @click=${() => this._toggleFavorite(this.options?.gameId)}
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._toggleFavorite.bind(this, this.options?.gameId))}
        class="favorite-icon"
        .fill=${this._favorites.has(this.options.gameId)}
      ></ui-favorite-icon>
    `;
  }

  private _skipToMainNav() {
    window.$app.a11y.focusElement(A11yElementId.MAIN_NAVIGATION);
  }

  private _showProductSwitcher() {
    const modal = new UIProductSwitcher(this._loginStatus === LoginStatus.LOGGED_IN);
    window.$app.track.productSwitcher({
      event: 'productSwitcher',
      eventAction: 'casino',
      status: this._loginStatus === LoginStatus.LOGGED_IN ? 'loggedIn' : 'loggedOut',
    });
    window.$app.modal.show(modal, ModalType.ProductSwitcher);
  }

  private _updateFavorites(favorites: Favorites | undefined) {
    window.$app.logger.log('Updating favorites in header', favorites);
    if (favorites instanceof Set && favorites.size > 0) {
      // Set favorites
      this._favorites = new Set(favorites);
      return;
    }
    if (this._favorites.size > 0) {
      // Reset favorites
      this._favorites = new Set();
    }
  }

  private _toggleFavorite(gameId?: string) {
    if (!gameId) {
      window.$app.logger.log('cannot _toggleFavorite, missing gameID:', gameId);
      return;
    }

    if (this._favorites.has(gameId)) {
      App.instance.favorites.unset(gameId);
      this._trackFavoriteToggle(TrackableEventAction.FAVICON_DESELECTED, gameId);
    } else {
      App.instance.favorites.set(gameId);
      this._trackFavoriteToggle(TrackableEventAction.FAVICON_SELECTED, gameId);
    }
  }

  private _handleLoginClick() {
    App.instance.product.gotoLogin(this.options?.trackingSource);
  }

  private _goToAccount() {
    App.instance.router.navigateToAccount();
  }

  private _goToNotifications() {
    window.$app.track.notifications('click');
    App.instance.product.gotoMessages();
  }

  private _updateLogin(val: LoginObject) {
    this._loginStatus = val.loginStatus;
  }

  private _trackFavoriteToggle(eventAction: TrackableEventAction, gameId: string) {
    App.instance.content
      .getGameInfo(gameId)
      .then(async (gameInfo) => {
        const game = await App.instance.content.getGame(gameId);
        if (game && gameId) {
          window.$app.track.toggleFavoriteGame({
            eventAction,
            gameCategory: game.labels?.join(' - '),
            gameFilters: App.instance.trackingStore.value.gameFilters,
            gameName: game.title,
            gamePosition: App.instance.trackingStore.value.gamePosition,
            gameProvider: gameInfo.getStudio(),
            gameSource: App.instance.trackingStore.value.gameSource ?? 'gameInfoPage',
          });
        }
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${gameId}'`, err);
      });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: BasePageHeader;
  }
}
